import { Readable } from 'generated/apolloComponents'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid'
import CategoryList from '@/components/molecules/CategoryList/CategoryList'
import BlogArticleSection from '@/components/organisms/BlogArticleSection'
import ArticlePreview from '../../organisms/ArticlePreview/ArticlePreview'
import styles from './Blog.module.scss'
import { isEmpty } from '@/helpers/utils'
import {
  ActionType,
  BlogContext,
  convertBlogFiltersSlugToURL,
} from '@/context/blogContext'
import Pagination from '@/components/molecules/Pagination'
import { useAnalyticsPush } from '@/context/analyticsContext'

interface IProps {
  last?: Readable
  filteredData?: Readable[] | null
  totalCount: number | undefined
  slugState: any
  categories: any
}

const BlogMain = ({
  last,
  slugState,
  filteredData,
  totalCount,
  categories,
}: IProps) => {
  const { state, dispatch } = useContext(BlogContext)
  const [searchedValue, setSearchedValue] = useState(state.filters?.q || '')
  const router = useRouter()
  const isMounted = useRef(false)
  const { t, lang } = useTranslation('common')
  const { pushEvent } = useAnalyticsPush()

  useEffect(() => {
    if (!isEmpty(slugState)) {
      dispatch({ type: ActionType.SetState, payload: slugState })
      setSearchedValue(state.filters?.q || slugState.filters.q || '')
    }
  }, [])

  useEffect(() => {
    const loadData = async function () {
      const slug = convertBlogFiltersSlugToURL(state)

      router.push({
        pathname: `/readme${slug}`,
      })
    }
    isMounted.current ? loadData() : (isMounted.current = true)
  }, [state])

  const handlePageChange = (page: number) => {
    dispatch({ type: ActionType.ChangePage, payload: page })
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.code || e.key

    if (keyCode == 'Enter') {
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { ...state.filters, q: e.currentTarget.value },
      })

      return false
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchedValue(e.currentTarget.value)
  }

  useEffect(() => {
    if (window) {
      pushEvent({
        event: 'search',
        searchData: {
          type: 'blog',
          ...slugState?.filters,
        },
      })
    }
  }, [slugState?.filters])

  return (
    <>
      <header>
        <div className='md:flex flex-wrap items-center'>
          <div className={styles.blog__header__title}>
            <h1 className='text-6xl'>{t('blog:blog_title')}</h1>
            <p className='text-2xl md:text-4xl font-normal'>
              {t('blog:blog_subtitle')}
            </p>
          </div>
          <div className={`${styles.blog__header__search}  mt-4 md:mt-0`}>
            <MagnifyingGlassIcon width={30} className='px-0.5 py-0.5' />
            {searchedValue.length > 3 && (
              <span className='absolute right-5 opacity-20 top-8 text-xs'>
                {t('common:enter_to_search')}
              </span>
            )}
            <input
              onKeyDown={handleKeyPress}
              onChange={handleInputChange}
              value={searchedValue}
              type='text'
              className='h-14 md:h-20 bg-gray rounded-2xl w-full pl-14 md:pl-20 pr-10 text-xl md:text-3xl'
              name='search'
              placeholder={t('blog:search_in_readme')}
            />
          </div>
        </div>
        <CategoryList className='mt-6 md:mt-12' categories={categories} />
      </header>
      <section className='md:px-14 pb-20'>
        {last && (
          <div className='my-6 md:my-14'>
            {last !== undefined && (
              <ArticlePreview
                size='big'
                article={last}
                className='border border-gray-100 rounded-br-xl overflow-hidden'
              />
            )}
          </div>
        )}
        {filteredData && <BlogArticleSection articles={filteredData} />}

        {filteredData && (
          <Pagination
            type='light'
            className='pagination-bar'
            currentPage={state.page}
            totalCount={totalCount as number}
            pageSize={state.perPage}
            onPageChange={(page: number) => handlePageChange(page)}
          />
        )}
      </section>
    </>
  )
}

export default BlogMain
